import { Injectable } from "@angular/core";
import { OrderApiService } from "src/services/order/order-api.service";
import { PendingOrderApiService } from "src/services/pending-order/pending-order-api.service";
import { Order } from "src/services/order/order.interface";

export interface AdminPromotionResult {
  orderId: string;
  pendingOrderDeleted: boolean;
}

@Injectable({
  providedIn: "root",
})
export class OrderPromotionService {
  constructor(
    private readonly pendingOrderService: PendingOrderApiService,
    private readonly orderService: OrderApiService,
  ) {}

  public async promoteOrder(pendingOrderId: string, _currentUserId?: string): Promise<string> {
    const pendingOrder = await this.pendingOrderService.getById(pendingOrderId);

    const newOrderId = await this.orderService.create(pendingOrder as Order);

    await this.pendingOrderService.delete(pendingOrder.id);

    return newOrderId;
  }

  // Admins must promote through POST /orders/admin: the generic create endpoint attributes
  // the new order to the authenticated caller, which would put the order under the admin's
  // name instead of the customer's.
  //
  // The create and the pending-order delete are two separate calls. When the delete fails
  // after the order was already created, the promotion is still reported as a success
  // (pendingOrderDeleted: false) so the caller navigates to the REAL order instead of
  // inviting a retry that would create a duplicate.
  public async promoteOrderAsAdmin(pendingOrderId: string): Promise<AdminPromotionResult> {
    const pendingOrder = await this.pendingOrderService.getById(pendingOrderId);

    if (!pendingOrder.userId) {
      throw new Error("This pending order is not linked to a user, it cannot be promoted.");
    }

    const orderId = await this.orderService.createForUser(pendingOrder as Order, pendingOrder.userId);

    let pendingOrderDeleted = true;
    try {
      await this.pendingOrderService.delete(pendingOrder.id);
    } catch {
      pendingOrderDeleted = false;
    }

    return { orderId, pendingOrderDeleted };
  }
}
